import React from 'react'
import { useToken, keyframes } from '@hub/design-system-base'
import { Box } from '@hub/box'
import { CoreContainer } from '@hub/core-container'
import {
  ClickAndCollectMediumIcon,
  DeliveryMediumIcon,
  HubIcon,
  WestfieldDirectReturnsMediumIcon,
  WestfieldDirectSwooshMediumIcon,
} from '@hub/icon'
import { IncludeDirectLogo } from '@hub/icon/src/with-direct-logo'
import { Link } from '@hub/link'
import { Stack } from '@hub/stack'
import { Text } from '@hub/text'

interface ICVPChildProps {
  IconComponent?: HubIcon
  title: string
  isDirectLogo?: boolean
}

const scrollToNext = keyframes`
  0% {
    top: 0;
  }

  20% {
    top: 0;
  }

  25% {
    top: -100%;
  }

  45% {
    top: -100%;
  }

  50% {
    top: -200%;
  }

  70% {
    top: -200%;
  }

  75% {
    top: -300%;
  }

  95% {
    top: -300%;
  }

  100% {
    top: -400%;
  }
`

const CVPChild: React.FC<ICVPChildProps> = ({ IconComponent, title }) => {
  const containerHeight = useToken('sizes', 'size-xs')
  const margin = useToken('space', 'spacing-sm')
  return (
    <Stack
      alignItems="center"
      justify="flex-end"
      sx={{
        position: 'relative',
        minHeight: `calc(${containerHeight} - ${margin} * 2)`,
        '&:last-child': {
          display: ['block', 'block', 'block', 'none'],
        },
      }}
    >
      <Text
        fontSize="font-xs"
        color="white"
        tone="strong"
        sx={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          alignContent: 'center',
        }}
      >
        {IconComponent && (
          <IconComponent
            sx={{
              marginRight: 'spacing-sm',
            }}
            boxSize="size-icon-line-height-shorter"
          />
        )}
        <IncludeDirectLogo title={title} />
      </Text>
    </Stack>
  )
}

const CVPStrip: React.FC = () => (
  <Link href={'https://beta.westfield.com.au/shop/info'}>
    <Box
      sx={{
        backgroundColor: 'blueCharcoal',
      }}
    >
      <CoreContainer sx={{ position: 'relative', height: 'size-xs' }}>
        <Stack
          gap="spacing-md"
          align={['center', 'center', 'center', 'flex-end']}
          justify={['flex-start', 'flex-start', 'flex-start', 'center']}
          direction={['column', 'column', 'column', 'row']}
          shouldWrapChildren={false}
          flexBasis={['size-full', 'size-full', 'size-full', 'auto']}
          flexGrow={[1, 1, 1, 1]}
          flexShrink={[0, 0, 0, 1]}
          minWidth={['size-full', 'size-full', 'size-full', 'unset']}
          sx={{
            animation: `${scrollToNext} 10s linear 1`,
            backgroundColor: 'blueCharcoal',
            padding: 'spacing-sm',
            overflowY: 'hidden',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <CVPChild title="Shop online with Westfield Direct" />
          <Stack
            alignItems="center"
            justify="flex-end"
            sx={{
              height: '100%',
              display: ['none', 'none', 'none', 'flex'],
              flexGrow: '0',
            }}
          >
            <WestfieldDirectSwooshMediumIcon
              sx={{
                marginRight: 'spacing-sm',
                marginLeft: 'spacing-sm',
                transform: 'scale(2)',
              }}
              boxSize="size-icon-line-height-shorter"
            />
          </Stack>
          <CVPChild
            IconComponent={DeliveryMediumIcon}
            title="Free delivery on orders $50 and over"
          />
          <CVPChild
            IconComponent={ClickAndCollectMediumIcon}
            title="10% rewards credit on Click & Collect"
          />
          <CVPChild
            IconComponent={WestfieldDirectReturnsMediumIcon}
            title="Free and easy returns"
          />
          {/* repeat the first item as the last to get a nice scroll effect on mobile (hidden on desktop) */}
          <CVPChild title="Shop online with Westfield Direct" />
        </Stack>
      </CoreContainer>
    </Box>
  </Link>
)

export default CVPStrip
